import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import axios from 'axios';
import { API } from '../lib/api';

const Contact = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        service: formData.service,
        message: formData.message
      });
      
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const serviceOptions = [
    { value: 'website', labelKey: 'contact.form.service.website' },
    { value: 'mobile', labelKey: 'contact.form.service.mobile' },
    { value: 'ecommerce', labelKey: 'contact.form.service.ecommerce' },
    { value: 'design', labelKey: 'contact.form.service.design' },
    { value: 'support', labelKey: 'contact.form.service.support' },
    { value: 'other', labelKey: 'contact.form.service.other' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section id="contact" className="py-20 relative overflow-hidden" ref={ref}>
      {/* Enhanced Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-green-900/20 to-slate-800" />
      
      {/* Color Accent Overlays */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-96 h-96 bg-green-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(34,197,94,0.1),transparent_70%)]" />
        
        {/* Enhanced Professional Graphics */}
        <motion.div
          className="absolute top-20 left-20 w-24 h-24 opacity-20"
          animate={{
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 40,
            repeat: Infinity,
            ease: "linear"
          }}
        >
          <svg viewBox="0 0 100 100" className="w-full h-full">
            <rect x="20" y="20" width="60" height="40" rx="4" fill="none" stroke="rgb(34, 197, 94)" strokeWidth="2"/>
            <path d="M30 35 L50 50 L70 35" stroke="rgb(59, 130, 246)" strokeWidth="2"/>
          </svg>
        </motion.div>

        {/* Enhanced Floating Professional Elements */}
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute opacity-20"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -20, 0],
              opacity: [0.2, 0.4, 0.2],
            }}
            transition={{
              duration: Math.random() * 4 + 3,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          >
            {i % 4 === 0 && (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="text-green-400">
                <path d="M3 8L7.89 5.26A2 2 0 0113.11 5.26L21 8" stroke="currentColor" strokeWidth="2"/>
              </svg>
            )}
            {i % 4 === 1 && (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="text-blue-400">
                <path d="M22 16.92V18A2 2 0 0120.18 20" stroke="currentColor" strokeWidth="2"/>
              </svg>
            )}
            {i % 4 === 2 && (
              <div className="w-1 h-1 bg-cyan-400 rounded-full" />
            )}
            {i % 4 === 3 && (
              <div className="w-1 h-1 bg-purple-400 rounded-full" />
            )}
          </motion.div>
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {/* Enhanced Section Title */}
          <motion.div
            variants={itemVariants}
            className="text-center mb-16"
          >
            <motion.h2 
              className="text-3xl md:text-4xl font-bold text-white mb-4 relative inline-block"
            >
              {t('contact.title')}
              <motion.div
                className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-green-500 to-blue-500 rounded-full"
                initial={{ scaleX: 0 }}
                animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
              />
            </motion.h2>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-16">
            {/* Enhanced Contact Info */}
            <motion.div
              variants={itemVariants}
              className="space-y-8"
            >
              <motion.div>
                <h3 className="text-2xl font-bold text-white mb-4">
                  {t('contact.subtitle')}
                </h3>
                <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                  {t('contact.description')}
                </p>
              </motion.div>
              
              <motion.div 
                className="space-y-6"
                variants={containerVariants}
              >
                {/* Enhanced Email */}
                <motion.div 
                  className="flex items-center space-x-4 group"
                  variants={itemVariants}
                  whileHover={{ x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  <motion.div 
                    className="w-12 h-12 bg-gradient-to-r from-green-500 to-blue-500 rounded-lg flex items-center justify-center shadow-lg"
                    whileHover={{ 
                      scale: 1.05,
                      boxShadow: "0 10px 20px rgba(34, 197, 94, 0.3)"
                    }}
                  >
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </motion.div>
                  <div>
                    <span className="text-slate-200 font-medium block">
                      {t('footer.email')}
                    </span>
                    <span className="text-slate-400 text-sm">{t('contact.email.desc')}</span>
                  </div>
                </motion.div>

                {/* Enhanced Support */}
                <motion.div 
                  className="flex items-center space-x-4 group"
                  variants={itemVariants}
                  whileHover={{ x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  <motion.div 
                    className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center shadow-lg"
                    whileHover={{ 
                      scale: 1.05,
                      boxShadow: "0 10px 20px rgba(168, 85, 247, 0.3)"
                    }}
                  >
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </motion.div>
                  <div>
                    <span className="text-slate-200 font-medium block">
                      {t('contact.support247')}
                    </span>
                    <span className="text-slate-400 text-sm">{t('contact.support247.desc')}</span>
                  </div>
                </motion.div>

                {/* Enhanced Response Time */}
                <motion.div 
                  className="flex items-center space-x-4 group"
                  variants={itemVariants}
                  whileHover={{ x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  <motion.div 
                    className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center shadow-lg"
                    whileHover={{ 
                      scale: 1.05,
                      boxShadow: "0 10px 20px rgba(251, 146, 60, 0.3)"
                    }}
                  >
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </motion.div>
                  <div>
                    <span className="text-slate-200 font-medium block">
                      {t('contact.fastResponse')}
                    </span>
                    <span className="text-slate-400 text-sm">{t('contact.fastResponse.desc')}</span>
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>

            {/* Enhanced Contact Form */}
            <motion.div
              variants={itemVariants}
            >
              <motion.div
                className="bg-slate-800/60 p-8 rounded-lg backdrop-blur-lg border border-slate-700 shadow-xl relative overflow-hidden"
                whileHover={{ y: -3 }}
                transition={{ duration: 0.3 }}
              >
                {/* Form Background Accent */}
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-blue-500/5" />
                
                <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
                  {/* Enhanced Form Fields */}
                  {[
                    { name: 'name', type: 'text', required: true, color: 'from-blue-500 to-cyan-500', icon: (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    )},
                    { name: 'email', type: 'email', required: true, color: 'from-green-500 to-emerald-500', icon: (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    )},
                    { name: 'phone', type: 'tel', required: false, color: 'from-purple-500 to-pink-500', icon: (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    )}
                  ].map((field, index) => (
                    <motion.div
                      key={field.name}
                      whileHover={{ scale: 1.01 }}
                      transition={{ duration: 0.2 }}
                    >
                      <label className="block text-sm font-medium text-slate-300 mb-2 flex items-center space-x-2">
                        <span className={`text-transparent bg-gradient-to-r ${field.color} bg-clip-text`}>{field.icon}</span>
                        <span>{t(`contact.form.${field.name}`)}</span>
                      </label>
                      <motion.input
                        type={field.type}
                        name={field.name}
                        value={formData[field.name]}
                        onChange={handleChange}
                        required={field.required}
                        className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-300"
                        placeholder={t(`contact.form.${field.name}.placeholder`)}
                        whileFocus={{ 
                          scale: 1.01,
                        }}
                      />
                    </motion.div>
                  ))}

                  {/* Enhanced Service Selector */}
                  <motion.div
                    whileHover={{ scale: 1.01 }}
                    transition={{ duration: 0.2 }}
                  >
                    <label className="block text-sm font-medium text-slate-300 mb-2 flex items-center space-x-2">
                      <span className="text-transparent bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </span>
                      <span>{t('contact.form.service')}</span>
                    </label>
                    <motion.select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-300"
                      whileFocus={{ scale: 1.01 }}
                    >
                      <option value="">{t('contact.form.service.placeholder')}</option>
                      {serviceOptions.map((option) => (
                        <option key={option.value} value={option.value} className="bg-slate-800">
                          {t(option.labelKey)}
                        </option>
                      ))}
                    </motion.select>
                  </motion.div>

                  {/* Enhanced Message Field */}
                  <motion.div
                    whileHover={{ scale: 1.01 }}
                    transition={{ duration: 0.2 }}
                  >
                    <label className="block text-sm font-medium text-slate-300 mb-2 flex items-center space-x-2">
                      <span className="text-transparent bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      </span>
                      <span>{t('contact.form.message')}</span>
                    </label>
                    <motion.textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-300"
                      placeholder={t('contact.form.message.placeholder')}
                      whileFocus={{ scale: 1.01 }}
                    />
                  </motion.div>

                  {/* Enhanced Submit Button */}
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-500 hover:to-blue-500 text-white py-3 px-6 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed border border-green-500 transition-all duration-300 shadow-lg shadow-green-500/25"
                    whileHover={!isSubmitting ? { 
                      scale: 1.02,
                      boxShadow: "0 20px 25px -5px rgba(34, 197, 94, 0.4), 0 10px 10px -5px rgba(34, 197, 94, 0.4)"
                    } : {}}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span className="flex items-center justify-center space-x-2">
                      {isSubmitting ? (
                        <>
                          <motion.div
                            className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          />
                          <span>Відправляємо...</span>
                        </>
                      ) : (
                        <>
                          <span>{t('contact.form.submit')}</span>
                          <motion.span
                            animate={{ x: [0, 3, 0] }}
                            transition={{ duration: 2, repeat: Infinity }}
                          >
                            →
                          </motion.span>
                        </>
                      )}
                    </span>
                  </motion.button>

                  {/* Enhanced Status Messages */}
                  {submitStatus === 'success' && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 bg-gradient-to-r from-green-900/30 to-emerald-900/30 border border-green-700/50 rounded-lg text-green-300"
                    >
                      <div className="flex items-center space-x-2">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>{t('contact.form.success')}</span>
                      </div>
                    </motion.div>
                  )}
                  
                  {submitStatus === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 bg-gradient-to-r from-red-900/30 to-pink-900/30 border border-red-700/50 rounded-lg text-red-300"
                    >
                      <div className="flex items-center space-x-2">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <span>{t('contact.form.error')}</span>
                      </div>
                    </motion.div>
                  )}
                </form>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;